import React from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';

export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.7);
  backdrop-filter: blur(6px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

export const Modal = styled.div`
  background: #0f172a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

export const Title = styled.h2`
  font-size: 1.05rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const Subtitle = styled.div`
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 4px;
`;

export const CloseBtn = styled.button`
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  &:hover { background: rgba(255, 255, 255, 0.05); color: #e2e8f0; }
`;

export const Body = styled.div`
  padding: 1.5rem;
  display: grid;
  gap: 1rem;
`;

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const Label = styled.label`
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
`;

export const Input = styled.input`
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  color: #e2e8f0;
  font-size: 0.9rem;
  &:focus {
    outline: none;
    border-color: rgba(139, 92, 246, 0.5);
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
  }
`;

export const Select = styled.select`
  background: rgba(15, 23, 42, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  color: #e2e8f0;
  font-size: 0.9rem;
  &:focus {
    outline: none;
    border-color: rgba(139, 92, 246, 0.5);
  }
`;

export const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 1rem 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

export const Button = styled.button<{ $primary?: boolean; $danger?: boolean }>`
  padding: 0.55rem 1.1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  border: 1px solid;
  transition: all 0.15s;

  ${p => p.$primary && `
    background: linear-gradient(135deg, #8b5cf6, #6366f1);
    border-color: rgba(139, 92, 246, 0.5);
    color: #ffffff;
    &:hover { filter: brightness(1.1); }
  `}
  ${p => p.$danger && `
    background: linear-gradient(135deg, #ef4444, #dc2626);
    border-color: rgba(239, 68, 68, 0.5);
    color: #ffffff;
    &:hover { filter: brightness(1.1); }
  `}
  ${p => !p.$primary && !p.$danger && `
    background: transparent;
    border-color: rgba(255, 255, 255, 0.1);
    color: #94a3b8;
    &:hover { background: rgba(255, 255, 255, 0.04); color: #e2e8f0; }
  `}

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const ErrorBanner = styled.div`
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  color: #fca5a5;
  font-size: 0.85rem;
`;

interface ModalShellProps {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}
export const ModalShell: React.FC<ModalShellProps> = ({ title, subtitle, icon, onClose, children }) => (
  <Backdrop onClick={onClose}>
    <Modal onClick={e => e.stopPropagation()}>
      <Header>
        <div>
          <Title>{icon}{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </div>
        <CloseBtn onClick={onClose} aria-label="Close"><X size={18} /></CloseBtn>
      </Header>
      {children}
    </Modal>
  </Backdrop>
);
